import React from 'react';
import {
  BuildingOfficeIcon,
  DocumentIcon,
  IdentificationIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

import { KnowledgeGraphEntityType } from '../../api/graphql';

const DEFAULT_ROW_ICON_CLASS = 'size-4 shrink-0 text-neutral-400 dark:text-neutral-500';
const SEARCH_ROW_ICON_CLASS = 'size-5 shrink-0 text-neutral-400 dark:text-neutral-500';

/** Human-readable type pill for structured AMS pins (not files). */
export function entityTypeBadgeLabel(entityType: KnowledgeGraphEntityType): string | undefined {
  switch (entityType) {
    case KnowledgeGraphEntityType.Account:
      return 'Account';
    case KnowledgeGraphEntityType.Policy:
      return 'Policy';
    case KnowledgeGraphEntityType.Certificate:
      return 'Certificate';
    default:
      return undefined;
  }
}

export function entityTypeIcon(
  entityType: KnowledgeGraphEntityType,
  className: string = DEFAULT_ROW_ICON_CLASS
): React.ReactNode {
  switch (entityType) {
    case KnowledgeGraphEntityType.Account:
      return <BuildingOfficeIcon className={className} aria-hidden="true" />;
    case KnowledgeGraphEntityType.Policy:
      return <ShieldCheckIcon className={className} aria-hidden="true" />;
    case KnowledgeGraphEntityType.Certificate:
      return <IdentificationIcon className={className} aria-hidden="true" />;
    case KnowledgeGraphEntityType.File:
    default:
      return <DocumentIcon className={className} aria-hidden="true" />;
  }
}

export function entityTypeSearchRowIcon(entityType: KnowledgeGraphEntityType): React.ReactNode {
  return entityTypeIcon(entityType, SEARCH_ROW_ICON_CLASS);
}

type SelectedEntityLike = { entityType: KnowledgeGraphEntityType };

/** Footer summary: "2 documents · 1 policy · 1 account" */
export function formatSelectedEntitySummary(entities: Iterable<SelectedEntityLike>): string {
  const counts = {
    [KnowledgeGraphEntityType.File]: 0,
    [KnowledgeGraphEntityType.Policy]: 0,
    [KnowledgeGraphEntityType.Certificate]: 0,
    [KnowledgeGraphEntityType.Account]: 0
  };

  for (const entity of entities) {
    counts[entity.entityType] = (counts[entity.entityType] ?? 0) + 1;
  }

  const parts: string[] = [];
  if (counts[KnowledgeGraphEntityType.File] > 0) {
    const n = counts[KnowledgeGraphEntityType.File];
    parts.push(`${n} document${n === 1 ? '' : 's'}`);
  }
  if (counts[KnowledgeGraphEntityType.Policy] > 0) {
    const n = counts[KnowledgeGraphEntityType.Policy];
    parts.push(`${n} polic${n === 1 ? 'y' : 'ies'}`);
  }
  if (counts[KnowledgeGraphEntityType.Certificate] > 0) {
    const n = counts[KnowledgeGraphEntityType.Certificate];
    parts.push(`${n} certificate${n === 1 ? '' : 's'}`);
  }
  if (counts[KnowledgeGraphEntityType.Account] > 0) {
    const n = counts[KnowledgeGraphEntityType.Account];
    parts.push(`${n} account${n === 1 ? '' : 's'}`);
  }

  if (parts.length === 0) return '0 selected';
  return parts.join(' · ');
}
